import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    this.#shuffleDefaultCollection();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  /** Keep chosen sorting/filtering and pagination in Shopify's native order. */
  #shuffleDefaultCollection() {
    if (!this.hasAttribute('data-collection-shuffle')) return;
    const url = new URL(window.location.href);
    const params = url.searchParams;
    if (url.hash || params.has('sort_by') || params.has('sabeti_size') || params.has('page')) return;
    if ([...params.keys()].some((key) => key.startsWith('filter.'))) return;
    const { grid } = this.refs;
    if (!grid || grid.dataset.collectionShuffled) return;
    const cards = Array.from(grid.children).filter((card) => card.matches('[data-product-id]'));
    if (cards.length < 2) return;
    for (let index = cards.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [cards[index], cards[other]] = [cards[other], cards[index]];
    }
    const key = 'sabeti-collection-order:' + this.getAttribute('data-collection-shuffle');
    const order = () => JSON.stringify(cards.map((card) => card.getAttribute('data-product-id')));
    try {
      if (sessionStorage.getItem(key) === order()) cards.push(cards.shift());
      sessionStorage.setItem(key, order());
    } catch {
      // Shuffling remains available when session storage is blocked.
    }
    for (const card of cards) grid.append(card);
    grid.dataset.collectionShuffled = 'true';
    super.updatedCallback();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

